import { useEffect, useRef } from 'react';

import { FacebookCustomEvent, FacebookScrollEvent } from './facebook';
import { FirebaseCustomEvent, FirebaseLinkClickEvent, FirebaseScrollEvent } from './firebase';

// Same depth as GA4's own enhanced-measurement scroll event.
const SCROLL_DEPTH_PERCENT = 90;

const hasReachedScrollDepth = () => {
  const { scrollHeight } = document.documentElement;
  const { innerHeight, scrollY } = window;
  // A page that fits the viewport has no depth to reach.
  if (scrollHeight <= innerHeight) return false;
  return ((scrollY + innerHeight) / scrollHeight) * 100 >= SCROLL_DEPTH_PERCENT;
};

// Links on the current host and between vansite domains are internal.
const isOutbound = (domain) => domain !== window.location.hostname && !domain.includes('vansite');

const getTrackingClasses = (element) =>
  element.className
    .split(' ')
    .filter((c) => !c.includes('NamedLink'))
    .map((c) => c.replace(/__.*/g, ''));

const trackLinkClick = (anchor, currentUserUuid) => {
  const classes = getTrackingClasses(anchor);
  const domain = (anchor.href !== '' && new URL(anchor.href)?.hostname) || window.location.hostname;
  const outbound = isOutbound(domain);
  FirebaseLinkClickEvent.withLinkClasses(classes)
    .withLinkDomain(domain)
    .setOutbound(outbound)
    .logEvent();
  FacebookCustomEvent.withCurrentUserUUID(currentUserUuid)
    .withCustomName('LinkClick')
    .withParams({ link_classes: classes, domain, outbound })
    .logEvent();
};

const trackButtonClick = (button, currentUserUuid) => {
  const classes = getTrackingClasses(button);
  // The native Firebase SDK rejects event names with hyphens.
  FirebaseCustomEvent.withCustomName('button_click').withParams({ classes }).logEvent();
  FacebookCustomEvent.withCurrentUserUUID(currentUserUuid)
    .withCustomName('ButtonClick')
    .withParams({ button_classes: classes })
    .logEvent();
};

const handleClickTracking = (event, currentUserUuid) => {
  setTimeout(() => {
    const anchor = event.target.closest('a');
    if (anchor !== null) trackLinkClick(anchor, currentUserUuid);
    const button = event.target.closest('button');
    if (button !== null) trackButtonClick(button, currentUserUuid);
  }, 0);
};

/**
 * Global click and scroll-depth tracking (GA4 + Meta), mounted once in RootLayout.
 * The scroll event fires at most once per page, and only after a real scroll.
 *
 * @param {string} pathname
 * @param {string} [currentUserUuid]
 */
const useInteractionTracking = (pathname, currentUserUuid) => {
  // The listeners outlive renders, and the user is usually not loaded yet when
  // they are registered — read the UUID at event time.
  const currentUserUuidRef = useRef(currentUserUuid);
  useEffect(() => {
    currentUserUuidRef.current = currentUserUuid;
  }, [currentUserUuid]);

  useEffect(() => {
    const onClick = (event) => handleClickTracking(event, currentUserUuidRef.current);
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (!hasReachedScrollDepth()) return;
      window.removeEventListener('scroll', onScroll);
      FirebaseScrollEvent.withPercentScrolled(SCROLL_DEPTH_PERCENT).logEvent();
      FacebookScrollEvent.withCurrentUserUUID(currentUserUuidRef.current).logEvent();
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [pathname]);
};

export default useInteractionTracking;
